/**
 * Which layout the viewer builds (pure; see test/patient-layout.test.mjs).
 *
 *   full    — the radiologist workspace (every feature module, gated by the session's permissions).
 *   patient — the phone-first patient layout (src/patient): only when the ris session says the ticket
 *             was issued to the patient by the patient portal. Nothing in the link can select it.
 *   referrer — the reading workspace without export, print, share, the assistant or anything that
 *             writes to ris, under a watermark (src/referrer): only when the ris session says the
 *             results portal issued the ticket to a referring doctor or convenio user. A session that
 *             claims both patient and referrer gets the patient layout (the narrower one), never `full`.
 *   blocked — a ticket link whose ris session could not be read: without the session the viewer
 *             cannot know whether a patient is looking, so it shows nothing and offers a retry
 *             instead of guessing (a patient must never get the full workspace).
 *
 * Hub-session (`?exam=`) and "Compartir" (`?share=`) launches already proved who is looking before
 * the session step, and the bench runs with no ris at all: those keep the full layout when the
 * workspace routes are unavailable, as before.
 */

export type ViewerLayout = 'full' | 'patient' | 'referrer' | 'blocked';

export interface LayoutInput {
  /** Outcome of the ris session step: 'none' when the viewer runs without ris (bench). */
  status: 'ok' | 'unavailable' | 'none';
  /** `patient` from GET /ris/v1/viewer/session (undefined when the session was not read). */
  patient: boolean | undefined;
  /** `referrer` from GET /ris/v1/viewer/session (undefined when the session was not read). */
  referrer: boolean | undefined;
  /** The link carried its own ticket (`?token=`): a portal or staff ticket link. */
  ticketLaunch: boolean;
}

export function viewerLayout({ status, patient, referrer, ticketLaunch }: LayoutInput): ViewerLayout {
  if (status === 'ok') {
    if (patient === true) return 'patient';
    return referrer === true ? 'referrer' : 'full';
  }
  if (status === 'unavailable' && ticketLaunch) return 'blocked';
  return 'full';
}
